import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Store, 
  Users, 
  TrendingUp, 
  Shield, 
  Clock, 
  Smartphone, 
  Award,
  ArrowRight,
  Star,
  MessageSquare,
  BarChart3
} from 'lucide-react';

const FashionHousesPage: React.FC = () => {
  const benefits = [
    {
      icon: TrendingUp,
      title: "Expand Revenue Streams",
      description: "Add tailoring services without hiring in-house staff. Generate additional revenue through partnerships."
    },
    {
      icon: Users,
      title: "Enhanced Customer Experience",
      description: "Offer one-stop shopping with professional measurement and alteration services."
    },
    {
      icon: Shield,
      title: "Quality Assurance",
      description: "Partner with verified, skilled tailors who meet our quality standards."
    },
    {
      icon: Clock,
      title: "Seamless Integration",
      description: "White-label solutions that integrate seamlessly with your existing operations."
    }
  ];

  const features = [
    {
      icon: Store,
      title: "White-Label Branding",
      description: "Customize the experience with your brand colors, logos, and messaging."
    },
    {
      icon: Smartphone,
      title: "Digital Measurement Stations",
      description: "Professional measurement equipment and digital profile management."
    },
    {
      icon: MessageSquare,
      title: "Customer Communication",
      description: "Direct communication tools between customers, tailors, and your team."
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description: "Track performance, revenue, and customer satisfaction metrics."
    },
    {
      icon: Award,
      title: "Revenue Sharing",
      description: "Earn commissions on every tailoring service referred through your store."
    },
    {
      icon: Star,
      title: "Premium Service Tier",
      description: "Access to our highest-rated tailors and priority customer support."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Owner, Elegance Boutique",
      content: "MasterJi helped us expand our services without the overhead of hiring tailors. Our customers love the convenience!",
      rating: 5
    },
    {
      name: "Marcus Rodriguez",
      role: "Manager, Urban Style",
      content: "The white-label integration is seamless. Our customers think it's our own service. Revenue increased 30% in the first quarter.",
      rating: 5
    },
    {
      name: "Jennifer Park",
      role: "Founder, Modern Threads",
      content: "Professional measurement stations and quality tailors have elevated our brand. Highly recommended!",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-blue/10 via-brand-white to-brand-yellow/10">

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-brand-blue to-brand-yellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 bg-brand-white/20 rounded-full flex items-center justify-center">
                <Store className="w-10 h-10 text-brand-white" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-brand-white mb-6">
              Fashion Houses
            </h1>
            <p className="text-xl md:text-2xl text-brand-white/80 mb-8 max-w-3xl mx-auto">
              Expand your fashion business with professional tailoring services. 
              Partner with verified tailors and enhance your customer experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-brand-white text-brand-blue px-8 py-4 rounded-lg text-lg font-semibold hover:bg-brand-white/90 transition-all duration-300 transform hover:scale-105"
              >
                Partner With Us
              </Link>
              <Link
                to="/features"
                className="bg-transparent text-brand-white px-8 py-4 rounded-lg text-lg font-semibold border-2 border-brand-white hover:bg-brand-white/10 transition-all duration-300 transform hover:scale-105"
              >
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-brand-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Partner With MasterJi?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your fashion business with professional tailoring services without the overhead.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-brand-blue/10 rounded-full flex items-center justify-center">
                      <Icon className="w-8 h-8 text-brand-blue" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Partnership Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to offer professional tailoring services to your customers.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{feature.title}</h3>
                  </div>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Partners Say
            </h2>
            <p className="text-xl text-gray-600">
              Fashion houses that have transformed their business with MasterJi.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-xl"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.content}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-gray-600 text-sm">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-blue to-brand-yellow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-brand-white mb-6">
            Ready to Transform Your Fashion Business?
          </h2>
          <p className="text-xl text-brand-white/80 mb-8">
            Join leading fashion houses that have expanded their services with MasterJi.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-brand-white text-brand-blue px-8 py-4 rounded-lg text-lg font-semibold hover:bg-brand-white/90 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
            >
              Start Partnership
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/features"
              className="bg-transparent text-brand-white px-8 py-4 rounded-lg text-lg font-semibold border-2 border-brand-white hover:bg-brand-white/10 transition-all duration-300 transform hover:scale-105"
            >
              View All Features
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FashionHousesPage;
